import { FC, ReactNode } from "react";
import cx from "apps/website/utils/misc/cx";
import { themeRootClassMap } from "apps/website/maps/Theme.map";
import {
  Padding,
  paddingMap,
  paddingXMap,
  paddingYMap,
} from "apps/website/maps/Spacing.map";
import { BorderRadius, borderRadiusMap } from "apps/website/maps/Border.map";

import TextBody from "../../base/Text/TextBody/TextBody";

import {
  ArrowAlign,
  ArrowIndicator,
  ArrowSize,
  ArrowTheme,
} from "./ArrowIndicator/ArrowIndicator";

export interface ITooltip {
  id?: string
  open?: boolean;
  children: ReactNode;
  withArrow?: boolean;
  arrowAlign?: ArrowAlign;
  arrowSize?: ArrowSize
  arrowOffsetPx?: number;
  backgroundColour?: ArrowTheme
  styleOptions?: {
    padding?: Padding;
    paddingX?: Padding;
    paddingY?: Padding;
    borderRadius?: BorderRadius;
  };
}

/**
   * Helper function to determine padding classes based on style options
   * Prioritizes paddingX/paddingY over uniform padding
   */
const getPaddingClasses = (styleOptions: ITooltip["styleOptions"] = {}): string[] => {
  const classes: string[] = [];

  if (styleOptions.paddingX !== undefined) {
    classes.push(paddingXMap[styleOptions.paddingX]);
  }
  if (styleOptions.paddingY !== undefined) {
    classes.push(paddingYMap[styleOptions.paddingY]);
  }
  if (classes.length > 0) {
    return classes;
  }

  const paddingClass = paddingMap[styleOptions.padding ?? 4];
  return [ paddingClass ];
};

/**
   * Helper function to determine border radius class based on style options
   */
const getBorderRadiusClass = (styleOptions: ITooltip["styleOptions"] = {}): string => borderRadiusMap[styleOptions.borderRadius ?? "none"];

const SimpleTooltip: FC<ITooltip> = ({
  id,
  children,
  open = false,
  withArrow = true,
  arrowAlign = "center",
  arrowSize = "small",
  arrowOffsetPx,
  backgroundColour = "light",
  styleOptions,
}) => {
  if (!open) { return null; }
  const backgroundClass = themeRootClassMap[backgroundColour];
  const paddingClasses = getPaddingClasses(styleOptions);
  const borderRadiusClass = getBorderRadiusClass(styleOptions);

  return (
    <div
      data-component={SimpleTooltip.name}
      className={cx("w-full")}
      role="tooltip"
      id={id ?? "tooltip"}
    >
      <div className={cx("relative", borderRadiusClass, backgroundClass, ...paddingClasses)}>
        <ArrowIndicator
          visible={withArrow}
          size={arrowSize}
          align={arrowAlign}
          offsetPx={arrowOffsetPx}
          backgroundColour={backgroundColour}
          styleOptions={{
            top: 0,
          }}
        />
        { typeof children === "string" ? (
          <TextBody size={3} align="center" >
            { children }
          </TextBody>
        ) : (
          children
        ) }
      </div>
    </div>
  );
};

export default SimpleTooltip;
